/**
 * ADDRESS GRAMMAR — 09 §1.5, premise P2.3/P2.4.
 *
 * "A URL never contains a business value." No `?db=b038`, no ח.פ, no Priority code, no FIN cid,
 * no unit slug. Only opaque `<entity>_uid`. The consequence is the point: a bookmarked link keeps
 * working when a company is renamed, re-linked to a different source db, or merged — because the
 * link names the internal company, not the source system's code.
 *
 * Every address in the app is built here. A hand-written template string elsewhere is a defect.
 */

/** Opaque uid shape. Anything that is not this must never reach an address. */
const UID = /^[a-z]{3}_[A-Za-z0-9_-]{1,64}$/;

/**
 * Fails loudly rather than putting a ח.פ or a `db` code in the address bar. This is a
 * developer guardrail, not a permission check — the gate decides access, always.
 */
function uid(kind: string, value: string): string {
  if (!UID.test(value)) {
    throw new Error(
      `Refusing to build an address from a non-uid ${kind}: "${value}". ` +
        '09 §1.5 / P2.4 — a URL carries only opaque uids, never a business value.',
    );
  }
  return value;
}

export const routes = {
  signIn: '/signin',

  /*
   * THE TWO MAILED ADDRESSES. Both carry a `?token=` that arrived in somebody's inbox.
   *
   * They sit outside `/tenant` and outside `/security` because the person opening them HAS NO
   * SESSION — an invitee has no account yet, and somebody who has forgotten a password cannot be
   * asked to sign in to fix it. An address under a signed-in section would be one the router sends
   * to sign-in, which is the loop G-10 records as a permanent lockout.
   */
  invitationAccept: '/invitation',
  passwordReset: '/signin/reset',

  /** Tenant Workspace landing: the user's companies (09 §5.1). */
  companies: '/companies',

  /** `/c/{company_uid}` — company home. */
  companyHome: (companyUid: string): string => `/c/${uid('companyUid', companyUid)}`,

  /** `/c/{company_uid}/m/{module}` — module home. */
  moduleHome: (companyUid: string, moduleCode: string): string =>
    `/c/${uid('companyUid', companyUid)}/m/${encodeURIComponent(moduleCode)}`,

  /** `/c/{company_uid}/m/{module}/{parent}` — parent screen. */
  parentScreen: (companyUid: string, moduleCode: string, parent: string): string =>
    `${routes.moduleHome(companyUid, moduleCode)}/${encodeURIComponent(parent)}`,

  /** 09 §5.7b — נייר השלמות is a first-class screen with its own address, not an error toast. */
  blockedDocument: (companyUid: string, documentUid: string): string =>
    `/c/${uid('companyUid', companyUid)}/documents/${uid('documentUid', documentUid)}/blocked`,

  /** The Platform Console. A separate surface, entered by an explicit action (09 §"two surfaces"). */
  platformConsole: '/platform',

  // ── Control Plane (PLATFORM surface) ────────────────────────────────────────────────────────
  //
  // Every address here begins `/platform/`, so the surface is legible in the address bar as well as
  // in the chrome. 04 I6: the two surfaces are never simultaneous, and a bookmarked platform
  // address opened in a tenant session is answered with a written state, never a silent switch.

  /** The customers the control plane administers. A parent screen, not a picker. */
  platformTenants: '/platform/tenants',

  /** The four provisioning steps: tenant → subscription → SKUs → first administrator. */
  platformTenantProvisioning: '/platform/tenants/new',

  /** `/platform/tenants/{tenant_uid}` — one customer. */
  platformTenant: (tenantUid: string): string => `/platform/tenants/${uid('tenantUid', tenantUid)}`,

  /** `/platform/tenants/{tenant_uid}/subscription` — purchased capacity, plainly. */
  platformTenantSubscription: (tenantUid: string): string =>
    `/platform/tenants/${uid('tenantUid', tenantUid)}/subscription`,

  /**
   * `/platform/tenants/{tenant_uid}/commercial` — money, and only money.
   *
   * A separate address from the subscription screen because 08 §8.4 / CF-66 keep money OUT of
   * entitlement: two tenants identical but for a discount must produce byte-identical access
   * decisions, and *"if changing the price invalidated the entitlement cache, price would be part of
   * entitlement"*. A capacity screen carrying a discount form is the first place those two get welded
   * back together, so the discount routes were given their own place instead.
   */
  platformTenantCommercial: (tenantUid: string): string =>
    `/platform/tenants/${uid('tenantUid', tenantUid)}/commercial`,

  /**
   * Support grants — the ONLY bridge between the platform and a customer's data (04 §13.1.5, §6.4).
   *
   * A place of its own rather than a panel on a customer card, because a grant has a lifecycle that
   * outlives the screen it was requested from: it is requested by one principal, approved by a
   * DIFFERENT one, and revoked from either side. A queue that only existed inside one customer's
   * card would leave the approver — who is looking for "what is waiting on me", not for a customer —
   * with nowhere to stand.
   */
  platformSupportGrants: '/platform/support-grants',

  // ── Tenant administration (TENANT surface) ──────────────────────────────────────────────────
  //
  // No tenant uid rides in these addresses. 04 I6: the active tenant is a property of the SESSION,
  // so putting it in the URL would create a second source for it — and a URL that names a tenant is
  // a URL somebody will edit.

  /** The office's own administration. A parent screen of parent screens. */
  tenantAdmin: '/tenant',
  tenantUsers: '/tenant/users',
  /** A child screen and NOT a modal: an invitation is a workflow with a written consequence. */
  tenantUserInvitation: '/tenant/users/invite',
  tenantCompanies: '/tenant/companies',
  tenantCompanyOnboarding: '/tenant/companies/new',
  /**
   * `/tenant/companies/{company_uid}` — the client's MASTER RECORD inside the office.
   *
   * Deliberately not `/c/{company_uid}`, which is the WORK surface: modules, periods, documents. This
   * is the administrative record — legal name, lifecycle state, external identifiers, source bindings,
   * portfolio — and it is reached from חברות במשרד under `companies.company.manage`. Two addresses for
   * two different questions about the same company, which is 09 §1.3 (a noun is a screen) applied to a
   * company that is both a thing you work in and a thing the office administers.
   */
  tenantCompany: (companyUid: string): string =>
    `/tenant/companies/${uid('companyUid', companyUid)}`,
  /** Roles — what a user may DO. Never which companies they may do it on (OA-0001 §3.7). */
  tenantRoles: '/tenant/roles',
  /**
   * DEFINING a role, which is not the same act as granting one.
   *
   * Its own address for the reason `tenantUserInvitation` has one: a workflow with a written
   * consequence is a child screen, not a panel. And specifically here — the assignment screen must
   * offer NO capability control (OA-0001 §3.7: the two axes never collapse into an admin flag), while
   * authoring is nothing BUT a capability list. Putting both on one screen would put a control that
   * grants every capability at once one scroll away from the picker that grants a role.
   *
   * No role code rides in the address. 09 §1.5: a URL carries only opaque uids, and a role code is a
   * business value — so the role being edited is chosen ON the screen, exactly as the person being
   * granted a role is chosen on the assignment screen.
   */
  tenantRoleAuthoring: '/tenant/roles/authoring',
  /** Company access — which companies they may do it on. The other half, deliberately apart. */
  tenantCompanyAccess: '/tenant/access',
  tenantSubscription: '/tenant/subscription',
  /** Source connections and their state. NOT CONFIGURED is not a kind of ERROR (07 N-5). */
  tenantSources: '/tenant/sources',
  /**
   * `/tenant/sources/{connection_uid}` — one connection: configure it, test it, map its companies.
   *
   * A child screen and NOT a modal. 09 §2.1: configuration is six fields, the external-company queue
   * is a table the user reads row by row (M4), and mapping opens a decision per row (M6). Three of the
   * six modal tests fail, so it is a screen with an address.
   */
  tenantSourceConnection: (connectionUid: string): string =>
    `/tenant/sources/${uid('connectionUid', connectionUid)}`,
  /** The office's own audit and security surface (04 §8.2 — its OWN events, never another's). */
  tenantSecurity: '/tenant/security',

  // ── Security (either surface — identity belongs to the account, not to a tenant) ────────────
  /**
   * The ACCOUNT's own security: where it is signed in, and its second factor.
   *
   * Reachable from both surfaces, because identity belongs to the account and not to a tenant — a
   * platform principal has no tenant at all and still has sessions and an MFA obligation. It is the
   * parent of the two enrolment/challenge addresses that already existed.
   */
  personalSecurity: '/security',
  mfaEnrolment: '/security/mfa/enrolment',
  mfaChallenge: '/security/mfa/challenge',
} as const;

/**
 * 09 §1.5: the period rides as a query parameter and is itself a uid.
 * Preserved across a company switch where it exists for the new company (09 §5.1).
 */
export function withPeriod(path: string, periodUid: string | null): string {
  if (periodUid === null) return path;
  return `${path}?period=${encodeURIComponent(uid('periodUid', periodUid))}`;
}

/** Route path patterns handed to React Router. Kept beside the builders so they cannot drift. */
export const routePatterns = {
  signIn: '/signin',
  invitationAccept: '/invitation',
  passwordReset: '/signin/reset',
  companies: '/companies',
  companyHome: '/c/:companyUid',
  moduleHome: '/c/:companyUid/m/:moduleCode',
  parentScreen: '/c/:companyUid/m/:moduleCode/:parent',
  blockedDocument: '/c/:companyUid/documents/:documentUid/blocked',
  platformConsole: '/platform',

  // A static segment out-ranks a parameter in React Router's matcher, so `/platform/tenants/new`
  // reaches the provisioning screen and never the detail screen with `tenantUid === 'new'` — which
  // `uid()` would refuse anyway, loudly, rather than putting a business word in an address.
  platformTenants: '/platform/tenants',
  platformTenantProvisioning: '/platform/tenants/new',
  platformTenant: '/platform/tenants/:tenantUid',
  platformTenantSubscription: '/platform/tenants/:tenantUid/subscription',
  platformTenantCommercial: '/platform/tenants/:tenantUid/commercial',
  platformSupportGrants: '/platform/support-grants',

  tenantAdmin: '/tenant',
  tenantUsers: '/tenant/users',
  tenantUserInvitation: '/tenant/users/invite',
  tenantCompanies: '/tenant/companies',
  tenantCompanyOnboarding: '/tenant/companies/new',
  tenantCompany: '/tenant/companies/:companyUid',
  tenantRoles: '/tenant/roles',
  tenantRoleAuthoring: '/tenant/roles/authoring',
  tenantCompanyAccess: '/tenant/access',
  tenantSubscription: '/tenant/subscription',
  tenantSources: '/tenant/sources',
  tenantSourceConnection: '/tenant/sources/:connectionUid',
  tenantSecurity: '/tenant/security',

  personalSecurity: '/security',
  mfaEnrolment: '/security/mfa/enrolment',
  mfaChallenge: '/security/mfa/challenge',
} as const;
